import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useUsersData } from "./Helpers/UsersContext";

interface AdminProtectedRouteProps {
    children: React.ReactNode;
    requireSuperadmin?: boolean;
}

const AdminProtectedRoute = ({ children, requireSuperadmin = false }: AdminProtectedRouteProps) => {
    const auth = getAuth();
    const location = useLocation();
    const { users, loadingUsers } = useUsersData();
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
        });

        return () => unsubscribe();
    }, [auth]);

    // Check access when user and users data are available
    useEffect(() => {
        if (auth.currentUser && users && !loadingUsers) {
            const currentUser = users.find(u => u.key === auth.currentUser?.uid);

            if (requireSuperadmin) {
                // Superadmin-only routes
                setHasAccess(currentUser?.superadmin || false);
            } else {
                // Admin routes - superadmins have all admin privileges
                setHasAccess(currentUser?.admin || currentUser?.superadmin || false);
            }
            setLoading(false);
        }
    }, [auth.currentUser, users, loadingUsers, requireSuperadmin]);

    if (loading || loadingUsers) {
        return <></>;
    }

    if (!isAuthenticated) {
        return <Navigate to="/signIn" state={{ from: location }} replace />;
    }

    if (!hasAccess) {
        return <Navigate to="/" replace />;
    }

    return children;
};

export default AdminProtectedRoute;
